import { useState } from "react";
import { Route, Routes } from "react-router";

import { useStore } from "@nanostores/react";
import { $organizationList } from "@vega-api/custom";
import { useSession } from "@vega-api/custom/SessionContext";
import { useBoolean } from "@vega-core/hooks/useBoolean";
import { useDeviceWidth } from "@vega-core/hooks/useDeviceWidth";
import { m } from "@vega-core/paraglide/messages";
import { getIsLPCoInvestPortal } from "@vega-core/utils/global";
import { isFalsy } from "@vega-core/utils/isFalsy";

import { SelectOrganisation } from "../../components/SelectOrganisation/SelectOrganisation";

import { DesktopShell } from "./components/DesktopShell";
import { MobileShell } from "./components/MobileShell";
import { PermissionsHelpModal } from "./components/PermissionsHelpModal/PermissionsHelpModal";
import { PermissionsHelpProvider } from "./context/PermissionsHelpContext";
import { useSettingsConfig } from "./hooks/useSettingsConfig";

export function Settings() {
  const { user } = useSession();
  const isMd = useDeviceWidth("md");
  const isLPCoInvestPortal = getIsLPCoInvestPortal();

  const isPermissionsHelpOpen = useBoolean(false);
  const [permissionsHelpInitialRoute, setPermissionsHelpInitialRoute] =
    useState<string | undefined>();
  const [showMobileMenu, setShowMobileMenu] = useState(true);
  const organisations = useStore($organizationList);

  const openPermissionsHelpWithRoute = (route?: string) => {
    setPermissionsHelpInitialRoute(route);
    isPermissionsHelpOpen.setTrue();
  };

  const closePermissionsHelp = () => {
    isPermissionsHelpOpen.setFalse();
    setPermissionsHelpInitialRoute(undefined);
  };

  if (isFalsy(user)) return null;

  const handleMenuClick = () => setShowMobileMenu(false);
  const { menuConfig, routes } = useSettingsConfig(handleMenuClick);

  const hasOrgOptions = organisations.length > 1;

  const orgSelector = hasOrgOptions ? (
    <div className="px-3">
      <p className="body-base-semibold px-3 pb-2 text-text/secondary">
        {m.settings_titles_selectOrganization()}
      </p>
      <SelectOrganisation hideLabel isSidebar={false} />
    </div>
  ) : null;

  const routeElements = (
    <Routes>
      {routes.map((route) => (
        <Route element={route.element} key={route.path} path={route.path} />
      ))}
    </Routes>
  );

  return (
    <PermissionsHelpProvider onOpenWithRoute={openPermissionsHelpWithRoute}>
      {isLPCoInvestPortal ? null : (
        <PermissionsHelpModal
          initialRoute={permissionsHelpInitialRoute}
          isOpen={isPermissionsHelpOpen.value}
          onClose={closePermissionsHelp}
        />
      )}

      {isMd ? (
        <DesktopShell
          menuConfig={menuConfig}
          onHelpClick={() => openPermissionsHelpWithRoute()}
          orgSelector={orgSelector}
        >
          {routeElements}
        </DesktopShell>
      ) : (
        <MobileShell
          menuConfig={menuConfig}
          onHelpClick={() => openPermissionsHelpWithRoute()}
          onMenuToggle={setShowMobileMenu}
          orgSelector={orgSelector}
          showMenu={showMobileMenu}
        >
          {routeElements}
        </MobileShell>
      )}
    </PermissionsHelpProvider>
  );
}
